import { GRID_HEADER_HEIGHT_PX } from '@/components/shared/grid-style';

/**
 * Geometry contract between the floating action bars (FloatingActionBar /
 * BulkActionBar) and the blotter they float over. Kept out of the component
 * modules so both bars and TradesTable can import it without tripping
 * react-refresh (and so the arithmetic sits in one place).
 */

/**
 * Distance (px) the bar floats above the viewport bottom. It clears the sticky
 * column-totals footer (one GRID_HEADER_HEIGHT_PX strip + room for a horizontal
 * scrollbar) so the bar sits ABOVE the totals instead of over them. The totals
 * strip is full-width and opaque: floating the bar on top of it hid the centre
 * figures, and the old fix — lifting the strip above the bar — exposed body rows
 * beneath it (the reported bug). Pinning the strip to bottom-0 and raising the
 * bar fixes both.
 */
export const FAB_BOTTOM_OFFSET_PX = GRID_HEADER_HEIGHT_PX + 20;

/**
 * Fallback vertical clearance (px) TradesTable pads below its last data row so
 * the row can scroll fully clear of the bar, used until the bar's real height is
 * measured (first paint, jsdom). Sized for the unwrapped two-row FAB (~108px:
 * status header strip + actions row) — see fabClearanceFor for the arithmetic.
 */
export const FAB_CLEARANCE_PX = 140;

/** Extra room above the bar so the last row is not flush against it. */
export const FAB_CLEARANCE_SLACK_PX = 12;

/**
 * Scroll clearance for a bar of the given rendered height: the bar's top edge
 * above the viewport bottom (offset + height), net of the in-flow footer height
 * the padding already sits on top of, plus slack.
 *
 * LR-600 made the actions row wrap on narrow viewports, so the height is no
 * longer a constant — at two button rows the old fixed 140px left the last data
 * row stranded behind the bar. Reproduces 140 exactly at the unwrapped height
 * (108 + 61 − 41 + 12), so the default layout is unchanged.
 */
export const fabClearanceFor = (barHeightPx: number): number =>
  barHeightPx > 0
    ? Math.ceil(barHeightPx) + FAB_BOTTOM_OFFSET_PX - GRID_HEADER_HEIGHT_PX + FAB_CLEARANCE_SLACK_PX
    : FAB_CLEARANCE_PX;
